<template>
  <div class="configuracoes-notificacoes">
    <!-- Overlay de Salvamento -->
    <div
      v-if="salvandoNotificacao"
      class="saving-overlay"
    >
      <div class="saving-content">
        <b-spinner
          variant="primary"
          style="width: 3rem; height: 3rem;"
        />
        <p class="mt-2 mb-0 text-primary font-weight-bold">
          Salvando e atualizando...
        </p>
      </div>
    </div>

    <!-- Header -->
    <b-card>
      <div class="mb-2 d-flex align-items-start justify-content-between flex-wrap">
        <div>
          <h3 class="mb-0">
            Configurações de Notificações
          </h3>
          <p class="text-muted mb-0">
            Configure notificações automáticas para WhatsApp
          </p>
        </div>
        <div
          v-if="lojaSelecionada && !carregando"
          class="d-flex align-items-center mt-2 mt-md-0"
        >
          <feather-icon
            :icon="notificacoesAtivasGlobal ? 'BellIcon' : 'BellOffIcon'"
            size="28"
            :class="notificacoesAtivasGlobal ? 'text-success' : 'text-secondary'"
          />
          <small
            class="ml-1 font-weight-medium"
            :class="notificacoesAtivasGlobal ? 'text-success' : 'text-muted'"
          >
            {{ notificacoesAtivasGlobal ? 'Ativadas' : 'Desativadas' }}
          </small>
        </div>
      </div>

      <!-- Seletor de Loja -->
      <div class="loja-selector mt-3">
        <b-form-group
          label="Selecione a Loja"
          label-for="loja-select"
          label-cols-md="2"
          label-align-md="right"
          class="mb-0"
        >
          <div class="d-flex align-items-center">
            <b-form-select
              id="loja-select"
              v-model="lojaSelecionada"
              :options="lojasOptions"
              :disabled="carregandoLojas"
              @change="onLojaChange"
            >
              <template #first>
                <b-form-select-option
                  :value="null"
                  disabled
                >
                  {{ carregandoLojas ? 'Carregando lojas...' : 'Selecione uma loja' }}
                </b-form-select-option>
              </template>
            </b-form-select>
            <b-spinner
              v-if="carregandoLojas"
              small
              variant="primary"
              class="ml-2"
            />
          </div>
        </b-form-group>
      </div>
    </b-card>

    <!-- Loading -->
    <div
      v-if="carregando"
      class="text-center my-5"
    >
      <b-spinner variant="primary" />
      <p class="mt-2 text-muted">
        Carregando notificações...
      </p>
    </div>

    <!-- Aviso para selecionar loja -->
    <b-card
      v-else-if="!lojaSelecionada"
      class="text-center py-5 mt-2"
    >
      <feather-icon
        icon="StoreIcon"
        size="64"
        class="text-muted mb-3"
      />
      <h4 class="text-muted">
        Selecione uma loja
      </h4>
      <p class="text-muted">
        Escolha uma loja acima para configurar as notificações
      </p>
    </b-card>

    <!-- Grid de Status -->
    <div
      v-else
      class="notificacoes-grid mt-2"
    >
      <b-card
        v-for="status in statusDisponiveis"
        :key="status.id"
        class="status-card"
        :class="{ 'has-config': getNotificacaoByStatus(status.id) }"
      >
        <!-- Ícone e Nome -->
        <div class="d-flex align-items-center mb-3">
          <div :class="`status-icon status-icon-${status.color} mr-2`">
            <feather-icon
              :icon="status.icon"
              size="24"
            />
          </div>
          <div class="flex-grow-1">
            <h5 class="mb-0">
              {{ status.nome }}
            </h5>
            <small class="text-muted">{{ status.descricao }}</small>
          </div>
        </div>

        <!-- Notificação Configurada - Status Aguardando Pagamento (múltiplas notificações) -->
        <template v-if="isAguardandoPagamento(status.id)">
          <div v-if="getNotificacoesByStatus(status.id).length > 0">
            <div
              v-for="notificacao in getNotificacoesByStatus(status.id)"
              :key="`${notificacao.status_id}-${notificacao.payment_id}`"
              class="notificacao-config mb-3"
            >
              <!-- Método de Pagamento -->
              <div class="mb-2">
                <b-badge :variant="notificacao.payment_id === 6 ? 'success' : 'warning'">
                  <feather-icon
                    :icon="notificacao.payment_id === 6 ? 'ZapIcon' : 'FileTextIcon'"
                    size="12"
                    class="mr-50"
                  />
                  {{ getNomeMetodoPagamento(notificacao.payment_id) }}
                </b-badge>
              </div>

              <div class="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <small class="text-muted d-block">Template:</small>
                  <b-badge variant="info">
                    {{ notificacao.template_name_meta }}
                  </b-badge>
                </div>
              </div>

              <!-- Preview do Body -->
              <div
                v-if="notificacao.body"
                class="template-body-preview mb-2"
              >
                <small class="text-muted d-block mb-1">Mensagem:</small>
                <div class="body-text">
                  {{ getTruncatedBody(notificacao.body) }}
                  <span
                    v-if="isBodyTruncated(notificacao.body)"
                    class="ver-mais"
                    @click="mostrarBodyCompleto(notificacao)"
                  >
                    ver mais
                  </span>
                </div>
              </div>

              <!-- Toggle Status -->
              <div class="d-flex align-items-center justify-content-between mb-2">
                <small class="text-muted">Status:</small>
                <div class="custom-switch custom-control">
                  <input
                    :id="`switch-${notificacao.id}`"
                    v-model="notificacao.ativo"
                    type="checkbox"
                    class="custom-control-input"
                    @change="toggleStatusNotificacao(notificacao)"
                  >
                  <label
                    :for="`switch-${notificacao.id}`"
                    class="custom-control-label"
                  >
                    <span :class="notificacao.ativo ? 'text-success' : 'text-secondary'">
                      {{ notificacao.ativo ? 'Ativa' : 'Inativa' }}
                    </span>
                  </label>
                </div>
              </div>

              <!-- Ações -->
              <div class="d-flex gap-2">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  block
                  @click="editarNotificacao(notificacao)"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="14"
                    class="mr-50"
                  />
                  Editar
                </b-button>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="confirmarExclusao(notificacao)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="14"
                  />
                </b-button>
              </div>
            </div>

            <!-- Botão adicionar outra notificação para Aguardando Pagamento -->
            <div
              v-if="getNotificacoesByStatus(status.id).length < 2"
              class="text-center mt-2"
            >
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="abrirModalNovaNotificacao(status.id)"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="14"
                  class="mr-50"
                />
                Adicionar {{ getNotificacoesByStatus(status.id).length === 0 ? '' : 'outra' }} notificação
              </b-button>
            </div>
          </div>

          <!-- Sem notificações para Aguardando Pagamento -->
          <div
            v-else
            class="text-center py-3"
          >
            <feather-icon
              icon="BellOffIcon"
              size="32"
              class="text-muted mb-2"
            />
            <p class="text-muted mb-2 small">
              Nenhuma notificação configurada
            </p>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="abrirModalNovaNotificacao(status.id)"
            >
              <feather-icon
                icon="PlusIcon"
                size="14"
                class="mr-50"
              />
              Configurar
            </b-button>
          </div>
        </template>

        <!-- Notificação Configurada - Outros Status (notificação única) -->
        <template v-else-if="getNotificacaoByStatus(status.id)">
          <div class="notificacao-config mb-3">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <div>
                <small class="text-muted d-block">Template:</small>
                <b-badge variant="info">
                  {{ getNotificacaoByStatus(status.id).template_name_meta }}
                </b-badge>
              </div>
            </div>

            <!-- Preview do Body -->
            <div
              v-if="getNotificacaoByStatus(status.id).body"
              class="template-body-preview mb-2"
            >
              <small class="text-muted d-block mb-1">Mensagem:</small>
              <div class="body-text">
                {{ getTruncatedBody(getNotificacaoByStatus(status.id).body) }}
                <span
                  v-if="isBodyTruncated(getNotificacaoByStatus(status.id).body)"
                  class="ver-mais"
                  @click="mostrarBodyCompleto(getNotificacaoByStatus(status.id))"
                >
                  ver mais
                </span>
              </div>
            </div>

            <!-- Toggle Status -->
            <div class="d-flex align-items-center justify-content-between mb-2">
              <small class="text-muted">Status:</small>
              <div class="custom-switch custom-control">
                <input
                  :id="`switch-${getNotificacaoByStatus(status.id).id}`"
                  v-model="getNotificacaoByStatus(status.id).ativo"
                  type="checkbox"
                  class="custom-control-input"
                  @change="toggleStatusNotificacao(getNotificacaoByStatus(status.id))"
                >
                <label
                  :for="`switch-${getNotificacaoByStatus(status.id).id}`"
                  class="custom-control-label"
                >
                  <span :class="getNotificacaoByStatus(status.id).ativo ? 'text-success' : 'text-secondary'">
                    {{ getNotificacaoByStatus(status.id).ativo ? 'Ativa' : 'Inativa' }}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <!-- Ações -->
          <div class="d-flex gap-2">
            <b-button
              variant="outline-primary"
              size="sm"
              block
              @click="editarNotificacao(getNotificacaoByStatus(status.id))"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
                class="mr-50"
              />
              Editar
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              @click="confirmarExclusao(getNotificacaoByStatus(status.id))"
            >
              <feather-icon
                icon="TrashIcon"
                size="14"
              />
            </b-button>
          </div>
        </template>

        <!-- Sem Configuração -->
        <template v-else>
          <div class="text-center py-3">
            <feather-icon
              icon="BellOffIcon"
              size="32"
              class="text-muted mb-2"
            />
            <p class="text-muted mb-2 small">
              Nenhuma notificação configurada
            </p>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="abrirModalNovaNotificacao(status.id)"
            >
              <feather-icon
                icon="PlusIcon"
                size="14"
                class="mr-50"
              />
              Configurar
            </b-button>
          </div>
        </template>
      </b-card>
    </div>

    <!-- Modal Nova/Editar Notificação -->
    <modal-notificacao
      v-model="modalNotificacaoVisivel"
      :notificacao-edicao="notificacaoEmEdicao"
      :status-pre-selecionado="statusPreSelecionado"
      :store-uuid="storeUuid"
      @salvar="salvarNotificacao"
    />
  </div>
</template>

<script>
import {
  BCard, BButton, BSpinner, BBadge, BFormGroup, BFormSelect, BFormSelectOption,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import stores from '@/functions/basics/stores'
import {
  STATUS_NOTIFICACOES,
  METODOS_PAGAMENTO,
  listarNotificacoes,
  criarNotificacao,
  atualizarNotificacao,
  excluirNotificacao,
  toggleNotificacaoAtiva,
  atualizarStatusNotificacoes,
  atualizarIsOficialStore,
} from '@/functions/basics/notifications-config'
import ModalNotificacao from './ModalNotificacao.vue'

export default {
  name: 'ConfiguracoesNotificacoes',
  components: {
    BCard,
    BButton,
    BSpinner,
    BBadge,
    BFormGroup,
    BFormSelect,
    BFormSelectOption,
    FeatherIcon,
    ModalNotificacao,
  },
  data() {
    return {
      notificacoes: [],
      lojas: [],
      lojaSelecionada: null,
      carregando: false,
      carregandoLojas: false,
      salvandoNotificacao: false,
      modalNotificacaoVisivel: false,
      notificacaoEmEdicao: null,
      statusPreSelecionado: null,
      statusDisponiveis: STATUS_NOTIFICACOES,
    }
  },
  computed: {
    storeUuid() {
      return this.lojaSelecionada
    },

    /** Notificações consideradas ativadas quando pelo menos uma está ativa */
    notificacoesAtivasGlobal() {
      if (!this.lojaSelecionada || !this.notificacoes.length) return false
      return this.notificacoes.some(n => n.ativo === true)
    },

    lojasOptions() {
      return this.lojas.map(loja => ({
        value: loja.uuid,
        text: loja.name || loja.nome || loja.store_name || 'Loja sem nome',
      }))
    },
  },
  mounted() {
    this.carregarLojas()
  },
  methods: {
    async carregarLojas() {
      this.carregandoLojas = true
      try {
        const storesList = await stores()
        this.lojas = storesList || []

        // Se houver apenas uma loja, selecionar automaticamente
        if (this.lojas.length === 1) {
          this.lojaSelecionada = this.lojas[0].uuid
          this.carregarNotificacoes()
        } else if (this.lojas.length === 0) {
          this.$bvToast.toast('Nenhuma loja encontrada', {
            title: 'Aviso',
            variant: 'warning',
            solid: true,
          })
        }
      } catch (error) {
        console.error('Erro ao carregar lojas:', error)
        this.$bvToast.toast('Erro ao carregar lojas', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregandoLojas = false
      }
    },

    onLojaChange() {
      // Limpar notificações ao trocar de loja
      this.notificacoes = []

      // Carregar notificações da nova loja
      if (this.lojaSelecionada) {
        this.carregarNotificacoes()
      }
    },

    async carregarNotificacoes() {
      if (!this.lojaSelecionada) return

      this.carregando = true
      try {
        const notificacoes = await listarNotificacoes(this.storeUuid)

        console.log('📋 Notificações recebidas da API:', notificacoes)

        // Converter campo 'ativo' para boolean para funcionar com v-model do checkbox
        this.notificacoes = (notificacoes || []).map(notif => ({
          ...notif,
          ativo: !!(notif.ativo === 1 || notif.ativo === '1' || notif.ativo === true),
        }))
        
        console.log('✅ Notificações processadas:', this.notificacoes)

        await atualizarStatusNotificacoes(this.notificacoesAtivasGlobal, this.storeUuid)
        await atualizarIsOficialStore(this.storeUuid, this.notificacoesAtivasGlobal ? 1 : 0)
      } catch (error) {
        console.error('Erro ao carregar notificações:', error)
        this.$bvToast.toast('Erro ao carregar notificações', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregando = false
      }
    },

    getNotificacaoByStatus(statusId) {
      return this.notificacoes.find(n => n.status_id === statusId)
    },

    // Retorna todas as notificações de um status (usado para Aguardando Pagamento)
    getNotificacoesByStatus(statusId) {
      return this.notificacoes.filter(n => n.status_id === statusId)
    },

    // Verifica se é status Aguardando Pagamento
    isAguardandoPagamento(statusId) {
      return statusId === 2
    },

    // Retorna nome do método de pagamento
    getNomeMetodoPagamento(paymentId) {
      const metodo = Object.values(METODOS_PAGAMENTO).find(m => m.id === paymentId)
      return metodo ? metodo.nome : 'Geral'
    },

    // Trunca o body do template
    getTruncatedBody(body) {
      if (!body) return ''
      const maxLength = 100
      if (body.length <= maxLength) return body
      return `${body.substring(0, maxLength)}...`
    },

    // Verifica se o body está truncado
    isBodyTruncated(body) {
      if (!body) return false
      return body.length > 100
    },

    // Mostra o body completo em um modal
    mostrarBodyCompleto(notificacao) {
      this.$bvModal.msgBoxOk(notificacao.body, {
        title: `Mensagem: ${notificacao.template_name_meta}`,
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'primary',
        okTitle: 'Fechar',
        headerClass: 'p-3',
        bodyClass: 'p-3',
        footerClass: 'p-2',
        centered: true,
      })
    },

    abrirModalNovaNotificacao(statusId = null) {
      this.notificacaoEmEdicao = null
      this.statusPreSelecionado = statusId
      this.modalNotificacaoVisivel = true
    },

    editarNotificacao(notificacao) {
      this.notificacaoEmEdicao = { ...notificacao }
      this.statusPreSelecionado = null
      this.modalNotificacaoVisivel = true
    },

    async salvarNotificacao(notificacao) {
      this.salvandoNotificacao = true

      try {
        if (notificacao.id) {
          // Editar notificação existente
          await atualizarNotificacao(notificacao.id, notificacao, this.storeUuid)

          this.$bvToast.toast('Notificação atualizada com sucesso!', {
            title: 'Sucesso',
            variant: 'success',
            solid: true,
          })
        } else {
          // Criar nova notificação
          await criarNotificacao(notificacao, this.storeUuid)

          this.$bvToast.toast('Notificação criada com sucesso!', {
            title: 'Sucesso',
            variant: 'success',
            solid: true,
          })
        }

        // Sempre recarregar as notificações do servidor após criar/editar
        // Isso garante que todos os dados estejam sincronizados e atualizados
        console.log('🔄 Recarregando notificações do servidor...')
        await this.carregarNotificacoes()
        console.log('✅ Notificações atualizadas com sucesso!')

        // Fechar modal após recarregar
        this.modalNotificacaoVisivel = false
      } catch (error) {
        console.error('❌ Erro ao salvar notificação:', error)
        this.$bvToast.toast('Erro ao salvar notificação. Verifique os dados e tente novamente.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.salvandoNotificacao = false
      }
    },

    async toggleStatusNotificacao(notificacao) {
      try {
        // Converter boolean para número (1 ou 0) antes de enviar
        const ativoNumerico = notificacao.ativo ? 1 : 0
        
        await toggleNotificacaoAtiva(notificacao.id, ativoNumerico, this.storeUuid)

        this.$bvToast.toast(
          `Notificação ${notificacao.ativo ? 'ativada' : 'desativada'} com sucesso!`,
          {
            title: 'Sucesso',
            variant: 'success',
            solid: true,
          },
        )

        await atualizarStatusNotificacoes(this.notificacoesAtivasGlobal, this.storeUuid)
        await atualizarIsOficialStore(this.storeUuid, this.notificacoesAtivasGlobal ? 1 : 0)
      } catch (error) {
        console.error('Erro ao alterar status da notificação:', error)

        // Reverter o switch em caso de erro
        notificacao.ativo = !notificacao.ativo

        this.$bvToast.toast('Erro ao alterar status da notificação', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },

    confirmarExclusao(notificacao) {
      const statusInfo = this.statusDisponiveis.find(s => s.id === notificacao.status_id)
      const nomeStatus = statusInfo ? statusInfo.nome : notificacao.status_id
      const nomeMetodo = this.getNomeMetodoPagamento(notificacao.payment_id)

      const mensagem = notificacao.payment_id && notificacao.status_id === 2
        ? `Tem certeza que deseja excluir a notificação de "${nomeStatus} - ${nomeMetodo}"?`
        : `Tem certeza que deseja excluir a notificação de "${nomeStatus}"?`

      this.$bvModal.msgBoxConfirm(mensagem, {
        title: 'Confirmar Exclusão',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Sim, excluir',
        cancelTitle: 'Cancelar',
        centered: true,
      }).then(confirmado => {
        if (confirmado) {
          this.excluirNotificacaoConfirmado(notificacao)
        }
      })
    },

    async excluirNotificacaoConfirmado(notificacao) {
      try {
        await excluirNotificacao({
          store_uuid: this.lojaSelecionada,
          status_id: notificacao.status_id,
          payment_id: notificacao.payment_id,
        })

        // Remove da lista local
        this.notificacoes = this.notificacoes.filter(n => !(n.status_id === notificacao.status_id && n.payment_id === notificacao.payment_id))

        this.$bvToast.toast('Notificação excluída com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        })

        await atualizarStatusNotificacoes(this.notificacoesAtivasGlobal, this.storeUuid)
        await atualizarIsOficialStore(this.storeUuid, this.notificacoesAtivasGlobal ? 1 : 0)
      } catch (error) {
        console.error('Erro ao excluir notificação:', error)
        this.$bvToast.toast('Erro ao excluir notificação', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style scoped>
.configuracoes-notificacoes {
  padding: 1rem;
}

.notificacoes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

.status-card {
  transition: all 0.3s ease;
  border: 2px solid #f0f0f0;
}

.status-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.status-card.has-config {
  border-color: #7367f0;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
}

.status-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.status-icon-primary {
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
}

.status-icon-success {
  background: rgba(40, 199, 111, 0.12);
  color: #28c76f;
}

.status-icon-danger {
  background: rgba(234, 84, 85, 0.12);
  color: #ea5455;
}

.status-icon-warning {
  background: rgba(255, 159, 67, 0.12);
  color: #ff9f43;
}

.status-icon-info {
  background: rgba(0, 207, 232, 0.12);
  color: #00cfe8;
}

.notificacao-config {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.template-body-preview {
  background: #ffffff;
  padding: 0.75rem;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.template-body-preview .body-text {
  font-size: 0.85rem;
  line-height: 1.4;
  color: #495057;
  white-space: pre-wrap;
  word-break: break-word;
}

.template-body-preview .ver-mais {
  color: #7367f0;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.8rem;
  margin-left: 0.25rem;
  text-decoration: underline;
}

.template-body-preview .ver-mais:hover {
  color: #5e50ee;
}

.gap-2 {
  gap: 0.5rem;
}

.loja-selector {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

/* Overlay de Salvamento */
.saving-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease;
}

.saving-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  animation: scaleIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .notificacoes-grid {
    grid-template-columns: 1fr;
  }

  .loja-selector {
    padding: 0.75rem;
  }
}
</style>
